<template>
  <!-- A voice message: play / pause, its loudness drawn as bars that fill
       as it plays (click or arrow keys to move through it), the time, and
       the speed. Only one voice message plays at a time. -->
  <div class="sc-voice" :class="{ 'sc-voice--mine': mine, 'is-playing': playing, 'is-broken': broken }">
    <button
      type="button"
      class="sc-voice__play"
      :disabled="broken"
      :aria-label="playing ? 'Pause voice message' : 'Play voice message'"
      @click="toggle"
    >
      <span v-if="waiting" class="sc-spinner" aria-hidden="true"></span>
      <i v-else class="pi" :class="playing ? 'pi-pause' : 'pi-play'" aria-hidden="true"></i>
    </button>
    <div
      class="sc-voice__wave"
      role="slider"
      tabindex="0"
      aria-label="Position in voice message"
      aria-valuemin="0"
      :aria-valuemax="Math.round(length)"
      :aria-valuenow="Math.round(position)"
      :aria-valuetext="`${formatChatDuration(position)} of ${formatChatDuration(length)}`"
      @click="seekTo"
      @keydown.left.prevent="nudge(-5)"
      @keydown.right.prevent="nudge(5)"
    >
      <span
        v-for="(bar, index) in bars"
        :key="index"
        class="sc-voice__bar"
        :class="{ 'is-played': (index + 0.5) / bars.length <= progress }"
        :style="{ height: `${Math.round(18 + bar * 82)}%` }"
      ></span>
    </div>
    <span class="sc-voice__time">{{ broken ? 'Unavailable' : timeLabel }}</span>
    <button
      type="button"
      class="sc-voice__rate"
      :disabled="broken"
      :title="`Playback speed ${rate}×`"
      :aria-label="`Playback speed ${rate} times`"
      @click="cycleRate"
    >
      {{ rate }}×
    </button>
    <audio
      ref="audio"
      :src="src"
      preload="metadata"
      @loadedmetadata="onMetadata"
      @durationchange="onMetadata"
      @timeupdate="onTime"
      @play="onPlay"
      @playing="waiting = false"
      @waiting="waiting = playing"
      @pause="onPause"
      @ended="onEnded"
      @error="onError"
    ></audio>
  </div>
</template>
<script>
import { formatChatDuration } from '@/utils/chat';

const RATES = [1, 1.5, 2];
const BAR_COUNT = 36;
const PLAY_EVENT = 'sc-voice:play';

let nextId = 0;

// A steady made-up shape for a message recorded without one.
function placeholderBars(seed) {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  return Array.from({ length: BAR_COUNT }, (_, index) => {
    hash = (hash * 1103515245 + 12345) >>> 0;
    const wave = Math.sin((index / BAR_COUNT) * Math.PI);
    return Math.min(1, 0.25 + wave * 0.45 + ((hash % 100) / 100) * 0.3);
  });
}

// Any number of values, resampled to `count` bars.
function resample(values, count) {
  if (values.length === count) return values;
  return Array.from({ length: count }, (_, index) => {
    const from = Math.floor((index / count) * values.length);
    const to = Math.max(from + 1, Math.floor(((index + 1) / count) * values.length));
    const slice = values.slice(from, to);
    return slice.reduce((sum, value) => sum + value, 0) / slice.length;
  });
}

export default {
  name: 'ChatVoicePlayer',
  props: {
    src: {
      type: String,
      required: true,
    },
    // Seconds, as recorded. A browser recording often carries no length of
    // its own, so this is what the player goes by.
    duration: {
      type: Number,
      default: 0,
    },
    waveform: {
      type: Array,
      default: () => [],
    },
    mine: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      playing: false,
      waiting: false,
      broken: false,
      position: 0,
      mediaDuration: 0,
      rate: 1,
      playerId: (nextId += 1),
    };
  },
  computed: {
    length() {
      if (this.duration > 0) return this.duration;
      return Number.isFinite(this.mediaDuration) && this.mediaDuration > 0 ? this.mediaDuration : 0;
    },
    progress() {
      return this.length ? Math.min(this.position / this.length, 1) : 0;
    },
    bars() {
      const values = (this.waveform || []).map(Number).filter((value) => Number.isFinite(value));
      if (values.length < 4) return placeholderBars(this.src);
      const peak = Math.max(...values, 0.05);
      return resample(values.map((value) => Math.min(value / peak, 1)), BAR_COUNT);
    },
    timeLabel() {
      return formatChatDuration(this.playing || this.position > 0 ? this.position : this.length);
    },
  },
  watch: {
    src() {
      this.playing = false;
      this.position = 0;
      this.broken = false;
    },
  },
  methods: {
    formatChatDuration,
    async toggle() {
      const audio = this.$refs.audio;
      if (!audio || this.broken) return;
      if (!audio.paused) {
        audio.pause();
        return;
      }
      if (this.length && this.position >= this.length - 0.05) audio.currentTime = 0;
      audio.playbackRate = this.rate;
      this.waiting = true;
      try {
        await audio.play();
      } catch (error) {
        this.waiting = false;
        if (error && error.name !== 'AbortError') this.broken = true;
      }
    },
    seekTo(event) {
      const audio = this.$refs.audio;
      if (!audio || !this.length || this.broken) return;
      const box = event.currentTarget.getBoundingClientRect();
      const ratio = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
      this.setPosition(ratio * this.length);
    },
    nudge(seconds) {
      if (!this.length) return;
      this.setPosition(Math.min(Math.max(this.position + seconds, 0), this.length));
    },
    setPosition(seconds) {
      const audio = this.$refs.audio;
      if (!audio) return;
      try {
        audio.currentTime = seconds;
        this.position = seconds;
      } catch (error) {
        // Not seekable yet; the position follows playback instead.
      }
    },
    cycleRate() {
      this.rate = RATES[(RATES.indexOf(this.rate) + 1) % RATES.length];
      if (this.$refs.audio) this.$refs.audio.playbackRate = this.rate;
    },
    onMetadata() {
      const audio = this.$refs.audio;
      if (audio) this.mediaDuration = audio.duration;
    },
    onTime() {
      const audio = this.$refs.audio;
      if (audio) this.position = audio.currentTime;
    },
    onPlay() {
      this.playing = true;
      window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: this.playerId }));
    },
    onPause() {
      this.playing = false;
      this.waiting = false;
    },
    onEnded() {
      this.playing = false;
      this.waiting = false;
      this.position = this.length;
    },
    onError() {
      this.playing = false;
      this.waiting = false;
      this.broken = true;
    },
    // Another voice message started: this one stops.
    onOtherPlay(event) {
      if (event.detail !== this.playerId && this.$refs.audio && !this.$refs.audio.paused) this.$refs.audio.pause();
    },
  },
  mounted() {
    window.addEventListener(PLAY_EVENT, this.onOtherPlay);
  },
  beforeUnmount() {
    window.removeEventListener(PLAY_EVENT, this.onOtherPlay);
    if (this.$refs.audio) this.$refs.audio.pause();
  },
};
</script>
